import { extractCompanyCodeFromCustomerId } from "./customerIdValidation";
import { cfpBaseUrl } from "../network/urls";

const BILL_MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const CONNECTION_TYPE_MAP = {
  D: "Domestic",
  C: "Commercial",
  I: "Industrial",
  A: "Agricultural",
  "01": "Domestic",
  "02": "Commercial",
  "03": "Commercial",
  "04": "Industrial",
  "05": "Bulk Supply",
  "06": "Agricultural Tube Wells",
  "07": "Street Lighting",
  "08": "Residential Colonies",
  1: "Domestic",
  2: "Commercial",
  3: "Industrial",
  4: "Agricultural",
};

let cachedTariffList = null;
let cachedTariffToken = null;
let tariffListInFlight = null;
let tariffListInFlightToken = null;

export const isRole99 = (roleId) => Number(roleId) === 99;

export const parseArrearsAmount = (value) => {
  if (value == null || value === "") return 0;
  const numeric = Number(String(value).replace(/,/g, "").trim());
  return Number.isFinite(numeric) ? numeric : 0;
};

export const parseArrearAge = (value) => {
  const numeric = Number(String(value ?? 0).replace(/,/g, "").trim());
  return Number.isFinite(numeric) ? numeric : 0;
};

export const hasUnpaidDebtOrArrear = (unpaidDebt, arrear) =>
  parseArrearsAmount(unpaidDebt) > 0 || parseArrearsAmount(arrear) > 0;

export const getUnpaidDebtFromConsumer = (consumer = {}) =>
  parseArrearsAmount(
    consumer.UNPAID_DEBT ??
    consumer.UNPAIDDEBT ??
    consumer.unPaidDebt ??
    consumer.UNPAID_DEPTH ??
    consumer.unpaid_debt ??
    consumer.UNPAIDDEPTH,
  );

export const getArrearsAmountFromConsumer = (consumer = {}) =>
  parseArrearsAmount(
    consumer.ARREAR ??
    consumer.ARREARS ??
    consumer.arrearsAmount ??
    consumer.ARREAR_AMOUNT ??
    consumer.ARREARS_AMOUNT,
  );

export const getArrearAgeFromConsumer = (consumer = {}) =>
  parseArrearAge(
    consumer.ARREAR_AGE ??
    consumer.ARREARAGE ??
    consumer.arrearAge ??
    consumer.arrear_age ??
    consumer.ARREARS_AGE ??
    consumer.NO_OF_ARREARS,
  );

export const getConsumerReferenceNo = (consumer = {}) =>
  String(
    consumer.REF_NO
    || consumer.refNo
    || consumer.REFERENCE_NO
    || consumer.referenceNo
    || consumer.REFNO
    || "",
  ).replace(/\s/g, "").slice(0, 14);

export const normalizeTariffCode = (tariffCode) =>
  String(tariffCode ?? "").trim().padStart(2, "0");

export const findTariffByCode = (tariffCode, tariffs = []) => {
  if (tariffCode == null || String(tariffCode).trim() === "") return null;
  const list = Array.isArray(tariffs) ? tariffs : [];
  const raw = String(tariffCode).trim();
  const code = normalizeTariffCode(raw);
  return (
    list.find((item) => {
      const apiCode = String(item.tariffCode ?? "").trim();
      const apiName = String(item.tariffName ?? "").trim();
      return (
        String(apiCode) === raw ||
        apiCode.padStart(2, "0") === code ||
        apiName === raw ||
        apiName.includes(`(${code})`) ||
        apiName.includes(`(${raw})`)
      );
    }) || null
  );
};

export const resolveTariffNameFromList = (tariffCode, tariffs = []) => {
  const match = findTariffByCode(tariffCode, tariffs);
  return String(match?.tariffName ?? "").trim();
};

export const getDcoTariffDisplay = (item = {}, tariffs = []) => {
  const code = item.tariffCode || item.tariffCd || item.TARIFF_CODE || "";
  const fromCode = resolveTariffNameFromList(code, tariffs);
  if (fromCode) return fromCode;
  const stored = String(item.tariffDesc || item.tariff || "").trim();
  if (stored) {
    const byStored = (Array.isArray(tariffs) ? tariffs : []).find((itemRow) => {
      const apiName = String(itemRow.tariffName ?? "").trim();
      const apiDesc = String(itemRow.tariffDesc ?? "").trim();
      return apiName === stored || apiDesc === stored;
    });
    if (byStored?.tariffName) return String(byStored.tariffName).trim();
  }
  return fromCode || String(code).trim() || "-";
};

export const getConnectionTypeFromTariffCode = (tariffCode) => {
  const code = String(tariffCode ?? "").trim().padStart(2, "0");
  return CONNECTION_TYPE_MAP[code] || CONNECTION_TYPE_MAP[String(tariffCode ?? "").trim()] || "";
};

export const getConnectionCategoryFromConsumer = (consumer = {}) => {
  const candidates = [
    consumer.CONS_CAT,
    consumer.CON_CAT,
    consumer.cons_cat,
    consumer.CONSUMER_CAT,
    consumer.CONN_CAT,
    consumer.CATEGORY,
    consumer.CONSUMER_TYPE,
  ];
  for (const value of candidates) {
    const raw = String(value ?? "").trim();
    if (!raw) continue;
    const mapped = CONNECTION_TYPE_MAP[raw.toUpperCase()] || CONNECTION_TYPE_MAP[raw];
    if (mapped) return mapped;
    if (/^(domestic|commercial|industrial|agricultural|agriculture|bulk|street)/i.test(raw)) {
      return raw;
    }
  }
  const tariffCode =
    consumer.TARIFF_CODE || consumer.TARIFF_ACTIVE || consumer.TARIFF_CD || consumer.TARIFF || "";
  return getConnectionTypeFromTariffCode(tariffCode);
};

export const formatIbsPhoneNo = (contactNo) => {
  if (!contactNo) return "";
  return String(contactNo).replace(/\D/g, "").replace(/^92/, "0").slice(0, 11);
};

export const toApiBillMonth = (value) => {
  if (value == null || value === "") return "";
  const raw = String(value).trim();
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const day = String(parsed.getDate()).padStart(2, "0");
    return `${parsed.getFullYear()}-${month}-${day}`;
  }
  return raw;
};

export const displayBillMonth = (value) => {
  if (value == null || value === "") return "";
  const raw = String(value).trim();
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return `${BILL_MONTH_NAMES[parsed.getMonth()]}-${parsed.getFullYear()}`;
};

export const getTodayYmd = () => {
  const date = new Date();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

export const getDisplayConsumerStatus = (currentStatus) => {
  const status = String(currentStatus ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
  if (!status) return "";
  if (status === "connected") return "Connected";
  if (status === "p.disc") return "P.Disc";
  if (status === "t.disc") return "T.Disc";
  return String(currentStatus).trim();
};

export const officeCodesMatch = (left, right) => {
  const a = Number(left);
  const b = Number(right);
  return Number.isFinite(a) && Number.isFinite(b) && a === b;
};

export const uniqueHierarchyOffices = (
  hierarchyList,
  parentKey,
  parentCode,
  childCodeKey,
  childNameKey,
  sortByName = false,
) => {
  const unique = [];
  (Array.isArray(hierarchyList) ? hierarchyList : []).forEach((row) => {
    if (!officeCodesMatch(row[parentKey], parentCode)) return;
    if (!unique.find((item) => officeCodesMatch(item.code, row[childCodeKey]))) {
      unique.push({ code: row[childCodeKey], name: row[childNameKey] });
    }
  });
  if (!sortByName) return unique;
  return unique.sort((a, b) => a.name.localeCompare(b.name, "en", { ignorePunctuation: true }));
};

const normalizeDiscoCode = (code) => String(code ?? "").trim().padStart(2, "0");
const digitsOnly = (value) => String(value ?? "").replace(/\D/g, "");

export const getConsumerSubdivisionFromRef = (refNo) => {
  const ref = String(refNo ?? "").replace(/\s/g, "");
  return ref.length >= 7 ? ref.slice(2, 7) : "";
};

export const consumerMatchesLoginSubdivision = (refNo, loginOffice) => {
  const office = digitsOnly(loginOffice);
  if (!office || office === "10") return true;
  const consumerSub = getConsumerSubdivisionFromRef(refNo);
  if (!consumerSub) return true;
  const loginSub = office.length >= 5 ? office.slice(0, 5) : office;
  if (loginSub.length < 5) {
    return consumerSub.slice(0, loginSub.length) === loginSub;
  }
  return officeCodesMatch(consumerSub, loginSub);
};

export const consumerMatchesLoggedInDisco = (searchId, loggedInCompany) => {
  const loggedIn = normalizeDiscoCode(loggedInCompany);
  if (!loggedIn || loggedIn === "10") return true;
  const fromId = extractCompanyCodeFromCustomerId(searchId);
  if (!fromId) return true;
  return normalizeDiscoCode(fromId) === loggedIn;
};

export const fetchDcoTariffList = async (token) => {
  if (!token) return [];
  if (cachedTariffToken === token && cachedTariffList) return cachedTariffList;
  if (tariffListInFlightToken === token && tariffListInFlight) return tariffListInFlight;

  const request = (async () => {
    const myHeaders = new Headers();
    myHeaders.append("Authorization", token);
    const response = await fetch(
      `${cfpBaseUrl}customerFaciliationPortal/getTariffsDetail`,
      { method: "POST", redirect: "follow", headers: myHeaders },
    );
    const result = await response.json();
    const list = Array.isArray(result?.data) ? result.data : [];
    cachedTariffToken = token;
    cachedTariffList = list;
    return list;
  })();

  tariffListInFlight = request;
  tariffListInFlightToken = token;

  try {
    return await request;
  } catch {
    return cachedTariffToken === token ? cachedTariffList || [] : [];
  } finally {
    if (tariffListInFlight === request) {
      tariffListInFlight = null;
      tariffListInFlightToken = null;
    }
  }
};
